import { isEmpty, noop } from 'lodash';
import { ReactElement, useMemo } from 'react';

import {
   MedicalInformation,
   MedicalInformationQuery,
   MedicalInformationType,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   JustifyContent,
   LinkSize,
   StyledFlex,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { ConfidentialMask, getConfidentialAccessButton } from '~libs.desktop.shared.medical-data-confidentiality';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientMedicalInformationRemoveModalOpener } from '~libs.desktop.patient-medical-information';

import { AttachmentsLinks } from '../../shared/AttachmentLinks/AttachmentsLinks';
import { patientMedicalInformationTranslationKeys } from '../../translations';
import { MedicalInformationModalOpener } from '../MedicalInformationModal/MedicalInformationModal.opener';
import { refetchAllMedicalInformation } from '../shared/refetchAllMedicalInformation';
import { DescriptionCell } from '../shared/table/DescriptionCell';
import { MedicalInformationModalOpenLink } from '../shared/table/MedicalInformationModalOpenLink';
import { useShowRemovedMedicalInformationState } from '../MedicalInformationShowRemovedCheckbox.hooks';
import { MedicalInformationRemoveLink } from '../shared/table/MedicalInformationRemoveLink';
import { useMedicalInformationRemoveEntryAvailable } from '../shared/useMedicalInformationRemoveEntryAvailable';

import { filterRemovedAttachments } from './MedicalInformationTable.utils';

const DEFAULT_ORDERINGS: Ordering<MedicalInformation>[] = [
   {
      descending: true,
      property: 'creationDate',
   },
];

export const useMedicalInformationDataConfiguration = (
   patientId: number,
   medicalInformationType: MedicalInformationType
): LazyLoadedDataConfigurationByCqrsQuery<MedicalInformation> => {
   const [showRemoved] = useShowRemovedMedicalInformationState();

   return useLazyLoadedDataConfigurationByCqrsQuery<MedicalInformation>(() => {
      const query = new MedicalInformationQuery();
      query.patientId = Slot.emptyIfNull(patientId);
      query.type = Slot.emptyIfNull(medicalInformationType);
      query.showRemoved = showRemoved;
      return query;
   }, DEFAULT_ORDERINGS);
};

// eslint-disable-next-line max-lines-per-function
export const useMedicalInformationTableColumns = (
   onRemoveSuccess: VoidFunction
): TableColumnConfiguration<MedicalInformation>[] => {
   const medicalInformationRemoveEntryAvailable = useMedicalInformationRemoveEntryAvailable();
   const [showRemovedAttachments] = useShowRemovedMedicalInformationState();

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         creationDate: '115px',
         author: '300px',
         description: '100%',
         attachments: '300px',
         options: '215px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<MedicalInformation> = (props) =>
         props.bean.medicalInformationRemoved ? colorsMatrix.red.lighter : undefined;

      return [
         {
            accessor: 'creationDate',
            label: patientMedicalInformationTranslationKeys.table.medicalInformation.entryDate,
            width: columnsWidth.creationDate,
            backgroundColor: getBackgroundColor,
            formatter: dateFormatter,
         },
         {
            name: 'author',
            accessor: (medicalInformation) => medicalInformation.createdBy,
            label: patientMedicalInformationTranslationKeys.table.medicalInformation.enteredBy,
            width: columnsWidth.author,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,

            renderer: (props: ValueRendererProps<MedicalInformation>) => (
               <PractitionerSafePresenter
                  testId={'author'}
                  practitioner={props.bean.createdBy}
                  type={'standalone'}
                  withIdentification
               />
            ),
         },
         {
            accessor: 'description',
            label: patientMedicalInformationTranslationKeys.table.medicalInformation.description,
            width: columnsWidth.description,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,

            renderer: (props: ValueRendererProps<MedicalInformation, string>): ReactElement | null => (
               <ConfidentialMask entry={props.bean}>
                  {() => <DescriptionCell description={props.bean.description} testId={props.bean.id} />}
               </ConfidentialMask>
            ),
         },
         {
            name: 'attachments',
            accessor: (medicalInformation) => medicalInformation.attachments,
            label: patientMedicalInformationTranslationKeys.shared.table.attachments,
            width: columnsWidth.attachments,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,

            renderer: (props: ValueRendererProps<MedicalInformation, string>): ReactElement | null => (
               <ConfidentialMask entry={props.bean}>
                  {() =>
                     isEmpty(props.bean.attachments) ? null : (
                        <MedicalInformationModalOpener
                           medicalInformation={props.bean}
                           onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                        >
                           {({ openModal }) => (
                              <AttachmentsLinks
                                 attachments={filterRemovedAttachments(
                                    props.bean.attachments!,
                                    Boolean(showRemovedAttachments)
                                 )}
                                 onShowMoreAttachmentsClick={openModal}
                              />
                           )}
                        </MedicalInformationModalOpener>
                     )
                  }
               </ConfidentialMask>
            ),

            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            justifyContent: JustifyContent.END,
            renderer: (props: ValueRendererProps<MedicalInformation, string>): ReactElement => {
               const confidentialButton = getConfidentialAccessButton(props.bean, {
                  resourceType: 'MEDICAL_INFORMATION',
                  refetchQuery: MedicalInformationQuery,
               });

               if (confidentialButton) {
                  return confidentialButton;
               }

               return (
                  <StyledFlex>
                     <MedicalInformationModalOpener
                        medicalInformation={props.bean}
                        onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                     >
                        {({ openModal }) => (
                           <MedicalInformationModalOpenLink
                              onClick={openModal}
                              unsignedAttachmentCount={props.bean.unsignedAttachmentCount}
                              size={LinkSize.BIG}
                           />
                        )}
                     </MedicalInformationModalOpener>
                     {medicalInformationRemoveEntryAvailable(
                        props.bean.createdBy,
                        props.bean.medicalInformationRemoved
                     ) ? (
                        <PatientMedicalInformationRemoveModalOpener
                           medicalInformationId={props.bean.id}
                           onRemoveSuccess={onRemoveSuccess}
                        >
                           {({ openModal }) => <MedicalInformationRemoveLink onClick={openModal} />}
                        </PatientMedicalInformationRemoveModalOpener>
                     ) : null}
                  </StyledFlex>
               );
            },
         },
      ];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [showRemovedAttachments, medicalInformationRemoveEntryAvailable]);
};
